// Testimony draft record: key, serializer, total parser and link predicates
// (docs/plans/testimony-safe-dismissal-2026-10-15-rev4.md, sections 3.1 to 3.3).
// Pure: no storage, no browser globals. The storage operations live in
// content-lifecycle/store.ts, inside its owner-fenced transactions.
import { PRAYER_ATTEMPT_PREFIX } from './prayer-command';
import type { PrayerAttemptV1 } from '@/types/prayer-command';
import type { TestimonyDraftLinkV1, TestimonyDraftV1 } from '@/types/testimony-draft';

export const TESTIMONY_DRAFT_SCHEMA = 'adia-testimony-draft-v1';
/** Same cap as an attempt's testimony text. */
export const TESTIMONY_DRAFT_MAX_TEXT = 250_000;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const STATUSES = ['Open', 'In Progress', 'Answered', 'Moving'];

/**
 * Deliberately under the attempt prefix: today's owner-work and backup code then
 * protect the row, while `parsePrayerAttemptRow` rejects it (its key never
 * equals `prayerAttemptKey(owner, commandId)`).
 */
export function testimonyDraftKey(ownerId: string, prayerId: string): string {
  return `${PRAYER_ATTEMPT_PREFIX}${ownerId}:testimony-draft:${prayerId}`;
}

export function testimonyDraftOwnerPrefix(ownerId: string): string {
  return `${PRAYER_ATTEMPT_PREFIX}${ownerId}:testimony-draft:`;
}

/** Canonical bytes: fixed field order, so byte comparison is meaningful. */
export function serializeTestimonyDraft(draft: TestimonyDraftV1): string {
  return JSON.stringify({
    schema: TESTIMONY_DRAFT_SCHEMA,
    ownerId: draft.ownerId,
    prayerId: draft.prayerId,
    draftId: draft.draftId,
    textRevision: draft.textRevision,
    text: draft.text,
    baseTestimony: draft.baseTestimony,
    baseStatus: draft.baseStatus,
    link: draft.link === null ? null : {
      commandId: draft.link.commandId,
      attemptRevision: draft.link.attemptRevision,
      textRevision: draft.link.textRevision,
      purpose: draft.link.purpose,
    },
  });
}

function positiveInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 1;
}

function parseLink(value: unknown): TestimonyDraftLinkV1 | null | undefined {
  if (value === null) return null;
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const link = value as Record<string, unknown>;
  if (Object.keys(link).length !== 4) return undefined;
  if (typeof link.commandId !== 'string' || !UUID.test(link.commandId)) return undefined;
  if (!positiveInteger(link.attemptRevision) || !positiveInteger(link.textRevision)) return undefined;
  if (link.purpose !== 'save' && link.purpose !== 'without-note') return undefined;
  return {
    commandId: link.commandId,
    attemptRevision: link.attemptRevision,
    textRevision: link.textRevision,
    purpose: link.purpose,
  };
}

/**
 * Total: any row that is not exactly a well-formed draft for the owner and prayer
 * its key names returns null. Callers treat null as "not a draft" and never
 * rewrite such a row.
 */
export function parseTestimonyDraftRow(row: unknown): TestimonyDraftV1 | null {
  try {
    return parseTestimonyDraftRowUnchecked(row);
  } catch {
    // Total by contract: a stored value that makes a check throw is "not a draft".
    return null;
  }
}

function parseTestimonyDraftRowUnchecked(row: unknown): TestimonyDraftV1 | null {
  if (!row || typeof row !== 'object' || !('key' in row) || !('value' in row)) return null;
  const { key, value } = row as { key: unknown; value: unknown };
  if (typeof key !== 'string' || typeof value !== 'string') return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  const draft = parsed as Record<string, unknown>;
  if (Object.keys(draft).length !== 9) return null;
  if (draft.schema !== TESTIMONY_DRAFT_SCHEMA) return null;
  if (typeof draft.ownerId !== 'string' || !UUID.test(draft.ownerId)) return null;
  if (typeof draft.prayerId !== 'string' || !UUID.test(draft.prayerId)) return null;
  if (typeof draft.draftId !== 'string' || !UUID.test(draft.draftId)) return null;
  if (!positiveInteger(draft.textRevision)) return null;
  if (typeof draft.text !== 'string' || draft.text.length > TESTIMONY_DRAFT_MAX_TEXT) return null;
  if (draft.baseTestimony !== null && (typeof draft.baseTestimony !== 'string' || draft.baseTestimony.length > TESTIMONY_DRAFT_MAX_TEXT)) return null;
  if (typeof draft.baseStatus !== 'string' || !STATUSES.includes(draft.baseStatus)) return null;
  const link = parseLink(draft.link);
  if (link === undefined) return null;
  if (link && link.textRevision > (draft.textRevision as number)) return null;
  if (key !== testimonyDraftKey(draft.ownerId, draft.prayerId)) return null;
  return {
    schema: TESTIMONY_DRAFT_SCHEMA,
    ownerId: draft.ownerId,
    prayerId: draft.prayerId,
    draftId: draft.draftId,
    textRevision: draft.textRevision as number,
    text: draft.text,
    baseTestimony: draft.baseTestimony as string | null,
    baseStatus: draft.baseStatus as TestimonyDraftV1['baseStatus'],
    link,
  };
}

/** LINKED(D, T): the draft names T's command and T's current revision. */
export function testimonyDraftLinkedTo(draft: TestimonyDraftV1, attempt: PrayerAttemptV1): boolean {
  return draft.link !== null &&
    draft.link.commandId === attempt.commandId &&
    draft.link.attemptRevision === attempt.revision;
}

/** SAME-VERSION(D): the linked action was armed with the draft's current text. */
export function testimonyDraftSameVersion(draft: TestimonyDraftV1): boolean {
  return draft.link !== null && draft.link.textRevision === draft.textRevision;
}
